import { ArrowRightIcon, BlueTickIcon, CardWithCrossIcon, GreyStrokeInfoIcon } from 'assets/icons';
import { CustomCheckbox } from 'components';
import { selectCurrentTeamFeePermissions } from 'containers/app/appSlice';
import { CleverTapEventsPaymentMethods } from 'containers/invoices/events';
import { InvoiceLevelPaymentModes } from 'containers/invoices/InvoicesSlice';
import { PaymentMode } from 'containers/settings/invoiceSettings.model';
import { PaymentSubCategoryTypes } from 'containers/settings/type';
import { useAppSelector } from 'hooks';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { logAnalyticEvent } from 'utils/analytics';

interface InvoicePaymentMethodsSectionProps {
  paymentModes: InvoiceLevelPaymentModes[];
  invoiceTotal: number;
  onUpdateInvoicePaymentModes: (updatedPaymentModes: InvoiceLevelPaymentModes[]) => void;
  noPaymentMethods: boolean;
  availablePaymentModes: PaymentMode[];
}

const InvoicePaymentMethodsSection: FC<InvoicePaymentMethodsSectionProps> = ({
  paymentModes,
  invoiceTotal,
  onUpdateInvoicePaymentModes,
  noPaymentMethods,
  availablePaymentModes
}) => {
  const paymentModesPermissions = useAppSelector(selectCurrentTeamFeePermissions); // same as tips permissions
  const navigate = useNavigate();
  const hasSelfCheckoutPaymentModes = paymentModes.some(paymentMode => paymentMode.teamLevelVisiblity);

  useEffect(() => {
    logAnalyticEvent(CleverTapEventsPaymentMethods.webPaymentMethodsPaymentMethodSection, {
      invoiceTotal: invoiceTotal,
      paymentMethods: JSON.stringify(
        paymentModes.map(paymentMode => ({
          paymentMethodName: paymentMode.name,
          paymentMethodEnabled: paymentMode.enabled,
          paymentMethodAdminEnabled: paymentMode.adminEnabled,
          paymentMethodTeamLevelVisibility: paymentMode.teamLevelVisiblity
        }))
      ),
      hasSelfCheckoutPaymentModes: hasSelfCheckoutPaymentModes,
      hasNoPaymentMethods: noPaymentMethods,
      availablePaymentModes: JSON.stringify(
        availablePaymentModes.map(paymentMode => ({
          paymentMethodName: paymentMode.name,
          paymentMethodEnabled: paymentMode.enabled,
          paymentMethodAdminEnabled: paymentMode.adminEnabled,
          paymentMethodTeamLevelVisibility: paymentMode.teamLevelVisiblity
        }))
      )
    });
  }, []);

  const isPaymentMethodSelectedByDefault = (paymentMode: InvoiceLevelPaymentModes, isValid: boolean) => {
    if (paymentMode.subCategory?.type === PaymentSubCategoryTypes.INSTALLMENT || paymentMode.name === 'ach') {
      return false;
    }
    if ((paymentMode.isSelected || paymentMode.invoiceLevelEnableStatus) && isValid) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const updatedPaymentModes = paymentModes.map(paymentMode => {
      const isValid =
        +invoiceTotal && +invoiceTotal >= +paymentMode.minimumAmount && +invoiceTotal <= +paymentMode.maximumAmount;
      const isSelectedByDefault = isPaymentMethodSelectedByDefault(paymentMode, isValid);
      return {
        ...paymentMode,
        isValidForAmount: isValid,
        isSelected: isSelectedByDefault
      };
    });
    onUpdateInvoicePaymentModes(updatedPaymentModes);
  }, [invoiceTotal]);

  const onTogglePaymentMode = paymentMode => {
    const updatedPaymentModes = paymentModes.map(item => {
      return item.id === paymentMode.id ? { ...item, isSelected: !item.isSelected } : item;
    });
    onUpdateInvoicePaymentModes(updatedPaymentModes);
  };

  const routeToPaymentSettings = () => {
    navigate(routesPath.PAYMENT_SETTINGS);
  };

  return (
    <div className="w-full">
      <div className="text-17px font-semibold text-heading">Payment Methods</div>
      <div className="mb-5 mt-1 text-sbase text-heading">
        Choose payment methods you want to display to the customer when paying for this invoice.
      </div>
      {noPaymentMethods && (
        <div className="flex items-center gap-3.5 rounded-md border border-borderGray p-5">
          <div className="shrink-0">
            <CardWithCrossIcon />
          </div>
          <div>
            <div className="text-17px font-semibold text-error">
              {paymentModesPermissions.editTipsSettings
                ? 'No payment methods available'
                : 'No payment methods are enabled'}
            </div>
            {!paymentModesPermissions.editTipsSettings && (
              <div className="mt-2 text-sbase text-primaryText">Please reach out to your team owner or admin.</div>
            )}
          </div>
          {paymentModesPermissions.editTipsSettings && (
            <div
              className="ml-auto flex items-center gap-1 text-sbase font-semibold text-primaryText"
              onClick={routeToPaymentSettings}>
              View or update in <span className="text-sbase font-semibold text-secondary">payment method settings</span>
              <ArrowRightIcon className="mt-1" />
            </div>
          )}
        </div>
      )}
      {!noPaymentMethods && !hasSelfCheckoutPaymentModes ? (
        <div className="flex items-center gap-3.5 rounded-md border border-borderGray p-5">
          <div>
            <div className="text-17px font-semibold text-accentText">Self-checkout not enabled</div>
            {!paymentModesPermissions.editTipsSettings && (
              <div className="mt-2 text-sbase text-primaryText">
                Please reach out to your team owner or admin to enable self checkout.
              </div>
            )}
          </div>
          {paymentModesPermissions.editTipsSettings && (
            <div
              className="ml-auto flex shrink-0 items-center gap-1 text-sbase font-semibold text-primaryText"
              onClick={routeToPaymentSettings}>
              View or update in <span className="text-sbase font-semibold text-secondary">payment method settings</span>
              <ArrowRightIcon className="mt-1" />
            </div>
          )}
        </div>
      ) : (
        <div>
          {paymentModes.map(
            paymentMode =>
              paymentMode.teamLevelVisiblity && (
                <div
                  className={'flex items-center border-b border-borderGray py-4 last:border-none '}
                  key={paymentMode.id}>
                  <div className="mr-3.5 shrink-0">
                    <img src={paymentMode.icon.url} alt="" className="h-8 w-8" />
                  </div>
                  <div>
                    <div
                      className={`text-17px font-semibold text-tertiaryText ${
                        paymentMode.isValidForAmount ? '' : 'pointer-events-none opacity-60'
                      }`}>
                      {paymentMode.label}
                    </div>
                    {invoiceTotal > 0 && !paymentMode.isValidForAmount && (
                      <div className="mt-1 flex items-center gap-1">
                        <GreyStrokeInfoIcon className="shrink-0" width={15} height={15} />
                        <div className="text-px13 text-primaryText">
                          {paymentMode.label} is only available on transactions between{' '}
                          {getAmountWithCurrency(paymentMode.minimumAmount)} -{' '}
                          {getAmountWithCurrency(paymentMode.maximumAmount)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`ml-auto ${paymentMode.isValidForAmount ? '' : 'pointer-events-none opacity-60'}`}>
                    <CustomCheckbox
                      id={paymentMode.id + paymentMode.name}
                      isChecked={paymentMode.isSelected}
                      onClick={() => onTogglePaymentMode(paymentMode)}>
                      {paymentMode.isSelected && <BlueTickIcon />}
                    </CustomCheckbox>
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicePaymentMethodsSection;
